// CBRE bills two kinds of invoice: services (16% GST) and supplies (18% GST). The
// workbook carries only the amounts, so the kind is worked out from the invoice
// itself: the sales tax on a line divided by its invoice amount. Pure, no Firestore.
export const CBRE_GST = Object.freeze({
  SERVICE: { type: "SERVICE", rate: 0.16, label: "Service" },
  SUPPLY: { type: "SUPPLY", rate: 0.18, label: "Supply" }
});

const KINDS = [CBRE_GST.SERVICE, CBRE_GST.SUPPLY];

// How far the stated tax may be from an exact 16% / 18% (the sheet rounds to whole rupees).
const toleranceFor = (amount) => Math.max(2, Math.abs(amount) * 0.0005);

// -> { type: "SERVICE" | "SUPPLY" | "UNKNOWN", rate, percent }
export function classifyCbreLine({ invoiceAmount, salesTax }) {
  const amount = Number(invoiceAmount || 0);
  const tax = Number(salesTax || 0);
  const percent = amount ? Math.round((tax / amount) * 1000) / 10 : 0;
  const matches = KINDS
    .map((kind) => ({ kind, gap: Math.abs(tax - amount * kind.rate) }))
    .filter((candidate) => candidate.gap <= toleranceFor(amount))
    .sort((a, b) => a.gap - b.gap);
  if (!amount || !matches.length) return { type: "UNKNOWN", rate: null, percent };
  return { type: matches[0].kind.type, rate: matches[0].kind.rate, percent };
}

// A CBRE invoice may have several lines. -> { type: SERVICE | SUPPLY | MIXED | UNKNOWN,
// percent (overall tax / amount), lines, unknownLines }
export function classifyCbreInvoice(lineItems, totals = {}) {
  const items = Array.isArray(lineItems) && lineItems.length
    ? lineItems
    : [{ invoiceAmount: totals.invoiceAmount, salesTax: totals.salesTax }];
  const lines = items.map((item) => ({ ...item, ...classifyCbreLine(item) }));
  const unknownLines = lines.filter((line) => line.type === "UNKNOWN");
  const kinds = new Set(lines.map((line) => line.type));
  const amount = items.reduce((sum, item) => sum + Number(item.invoiceAmount || 0), 0);
  const tax = items.reduce((sum, item) => sum + Number(item.salesTax || 0), 0);
  const percent = amount ? Math.round((tax / amount) * 1000) / 10 : 0;
  let type;
  if (unknownLines.length) type = "UNKNOWN";
  else if (kinds.size > 1) type = "MIXED";
  else type = [...kinds][0];
  return { type, percent, lines, unknownLines };
}

export function cbreGstLabel(classification) {
  if (classification.type === "MIXED") return `Mixed services (16%) and supplies (18%) — ${classification.percent}% overall`;
  if (classification.type === "UNKNOWN") return `Unusual GST (${classification.percent}%) — check the sheet`;
  const kind = CBRE_GST[classification.type];
  return `${kind.label} · ${kind.rate * 100}% GST`;
}

// "" when every line is a recognised 16% service or 18% supply; otherwise why approval is refused.
export function cbreGstProblem(invoiceNumber, lineItems, totals) {
  const { unknownLines } = classifyCbreInvoice(lineItems, totals);
  if (!unknownLines.length) return "";
  const detail = unknownLines
    .map((line) => `"${String(line.description || "line " + (line.srNo || "")).slice(0, 40)}" has ${Math.round(line.salesTax || 0).toLocaleString("en-PK")} tax on ${Math.round(line.invoiceAmount || 0).toLocaleString("en-PK")} (${line.percent}%)`)
    .join("; ");
  return `${invoiceNumber}: CBRE invoices carry either 16% GST (services) or 18% (supplies), but ${detail}. Correct the Sales Tax in the workbook and upload it again.`;
}
